import { api } from "./api";
import { isPlatformHost } from "./platform";
import { detectDevice, isStandalone } from "../hooks/useInstallPrompt";

export const pushSupported = () =>
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window && isPlatformHost(window.location.hostname);

// iPhone Safari tab: notifications only work after "Add to Home Screen".
export const needsHomeScreen = () => detectDevice() === "ios" && !pushSupported() && !isStandalone();

export const pushPermission = () => (pushSupported() ? Notification.permission : "unsupported");

const b64ToBytes = (v) => {
  const raw = atob(v.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (v.length % 4)) % 4));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const registration = () => navigator.serviceWorker.register("/sw.js").then(() => navigator.serviceWorker.ready);

export const currentSubscription = async () => (pushSupported() ? (await registration()).pushManager.getSubscription() : null);

const withTimeout = (p, ms = 15000) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), ms))]);

// Must run from a tap (iPhone requires it). Returns the permission result.
export const enablePush = async () => {
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return perm;
  const reg = await withTimeout(registration());
  const { public_key } = await api.pushConfig();
  const sub = (await reg.pushManager.getSubscription()) ||
    (await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(public_key) })));
  await api.pushSubscribe(sub.toJSON());
  return "granted";
};

export const disablePush = async () => {
  const sub = await currentSubscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => {});
  await sub.unsubscribe();
};

// Re-link this device to whoever is signed in (permission already given earlier).
export const syncPush = async () => {
  if (pushPermission() !== "granted") return;
  const sub = await currentSubscription().catch(() => null);
  if (sub) await api.pushSubscribe(sub.toJSON()).catch(() => {});
};

// On logout: stop sending this account's notifications to this device.
export const unlinkPush = async (token) => {
  if (!token || pushPermission() !== "granted") return;
  const sub = await currentSubscription().catch(() => null);
  if (sub) await api.pushUnsubscribe(sub.endpoint, token).catch(() => {});
};
